import { IPlayerState, IPlayerStateQuestData, IPlayerStateTaskData } from '../IPlayerState';
import { questSlotsCount, questUnlockLevel, startingQuests } from '../../../Constants/Quests';
import QuestHelper, { QuestStateKeys } from '../../../Utility/QuestHelper';
import { QuestType } from '../QuestType';
import { PlayerStateHelper } from '../PlayerStateHelper';
import DateHelper from '../../../Utility/DateHelper';
import { getHighestLevelCompleted } from '../PlayerLevelHelper';
import { Immutable, KeyOf } from '../../../Utility/Types';
import { report } from '../../../report';
import { PlayerTask } from '../PlayerTasks';
import { FTUXType } from '../../../Player/PlayerProperty/Progress/FTUXType';
import IQuestUpdateTaskData from './IQuestUpdateTaskData';
import { queueQuestCompleteEvent, track } from '../../../Utility/Tracking/Track';
import { TActionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { ArrayHelper } from '../../../Utility/ArrayHelper';
import { InventoryStateHelper } from '../Inventory/InventoryStateHelper';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { IGameState } from 'State/Game/IGameState';
import { isArray } from 'lodash';
import { IRewardData } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import UIStateHelper from 'State/UI/UIStateHelper';
import { PopupID } from 'State/UI/PopupID';
import LevelHelper from 'Utility/LevelHelper';
import Game from 'Game';

export namespace QuestStateHelper {
    import getActiveEvents = EventStateHelper.getActiveEvents;

    export function fillQuestsIfNeeded(oldState: IGameState): IGameState {
        const activeEventsWithQuests = getActiveEvents(oldState).filter(event => event.hasQuests);
        const playerStateEventQuests = oldState.playerState.eventQuests.slice();
        const eventsNeedingNewQuests = activeEventsWithQuests.filter(
            event => !playerStateEventQuests.some(eventQuest => eventQuest.eventType === event.type)
        );

        if (
            !isQuestsFeatureUnlocked(oldState.playerState) ||
            // If all normal quests are filled
            (oldState.playerState.quests.length === questSlotsCount &&
                // and if there are no event quests to add
                eventsNeedingNewQuests.length === 0)
        )
            return oldState;

        const quests = oldState.playerState.quests.slice(0, questSlotsCount);
        for (let i = 0; i < questSlotsCount; i++) {
            quests[i] = oldState.playerState.quests[i] || QuestHelper.getNewQuestAndTrack(oldState, quests);
        }

        eventsNeedingNewQuests.forEach(event => {
            playerStateEventQuests.push(QuestHelper.getNewEventQuestAndTrack(oldState, event));
        });

        return {
            ...oldState,
            playerState: {
                ...oldState.playerState,
                quests: quests,
                eventQuests: playerStateEventQuests,
            },
        };
    }

    /**
     * Sets the progress of the quest at the specified index
     */
    export function setQuestProgress(oldState: IPlayerState, questIndex: number, progress: number): IPlayerState {
        return {
            ...oldState,
            quests: ArrayHelper.changeElement(oldState.quests, questIndex, quest => ({
                ...quest,
                progress,
            })),
        };
    }

    export function getActiveQuests(playerState: IPlayerState): IPlayerStateQuestData[] {
        return playerState.quests;
    }

    /**
     * Increments the progress of the specified quest type if it exists
     */
    export function incrementQuestProgressBy(
        playerState: IPlayerState,
        questType: QuestType,
        progressIncrement: number,
        tActionType?: TActionType,
        queueTask: boolean = true
    ): IPlayerState {
        if (!isQuestsFeatureUnlocked(playerState)) {
            return playerState;
        }
        if (tActionType) {
            const questIndex: number = getActiveQuests(playerState).findIndex(quest => quest.type === questType);
            if (questIndex !== -1) {
                track(
                    // @ts-ignore
                    `tQuest${questIndex + 1}ProgressGained`,
                    progressIncrement
                );
            } else {
                // todo: remove block when confirmed working
                console.warn('Failed to find quest to track progress? questType:', questType);
            }
        }

        const hasDefaultActiveQuest = getActiveQuests(playerState).some(quest => quest.type === questType);
        const hasEventActiveQuest = playerState.eventQuests.some(quest => quest.type === questType);

        // No active quests - nothing to progress.
        if (!hasDefaultActiveQuest && !hasEventActiveQuest) return playerState;

        // Only queue for default quests. There may be an event quest of the same type - these are ignored for now.
        if (queueTask && hasDefaultActiveQuest) {
            playerState = queueQuestProgressTask(playerState, questType, progressIncrement);
        }

        playerState = updateQuests(playerState, 'quests');
        playerState = updateQuests(playerState, 'eventQuests');

        return playerState;

        function updateQuests(playerState: IPlayerState, stateKey: QuestStateKeys) {
            // Find index of all quests with same type
            const questIndicesOfSameType = getIndicesWithQuestType(playerState[stateKey], questType);
            questIndicesOfSameType.forEach(questIndex => {
                playerState = {
                    ...playerState,
                    [stateKey]: ArrayHelper.changeElement(playerState[stateKey], questIndex, quest => ({
                        ...quest,
                        progress: quest.progress + progressIncrement,
                    })),
                };
            });

            return playerState;
        }
    }

    function getIndicesWithQuestType(quests: IPlayerStateQuestData[], questType: QuestType): number[] {
        return quests.map((quest, index) => (quest.type === questType ? index : -1)).filter(index => index >= 0);
    }

    function queueQuestProgressTask(oldState: IPlayerState, questType: QuestType, progressIncrement: number) {
        const questData = getActiveQuests(oldState).find(quest => quest.type === questType);
        const newQuestData = { ...questData };
        newQuestData.progress = questData.progress + progressIncrement;
        const canComplete = QuestHelper.canCompleteQuest(newQuestData);

        // Get index of task with the same QuestType if any exists
        const index = oldState.tasks.findIndex(taskData =>
            isQuestProgressTaskOfSameType(taskData as IQuestUpdateTaskData, questType)
        );
        // If task to update progress for this QuestType exists then update that task
        if (index !== -1) {
            oldState = {
                ...oldState,
                tasks: ArrayHelper.changeElement(oldState.tasks, index, taskData => ({
                    ...taskData,
                    newProgress: taskData.newProgress + progressIncrement,
                })),
            };
        } else {
            oldState = PlayerStateHelper.addStateTaskDatas(
                oldState,
                [
                    {
                        task: PlayerTask.SHOW_QUEST_PROGRESS,
                        questData,
                        newProgress: progressIncrement,
                    },
                ],
                true
            );
        }
        if (canComplete) {
            const taskIndex = oldState.tasks.findIndex(taskData =>
                isQuestCompleteTaskOfSameType(taskData as IQuestUpdateTaskData)
            );
            const questIndex = oldState.quests.findIndex(quest => quest.id === questData.id);
            if (taskIndex !== -1) {
                oldState = {
                    ...oldState,
                    tasks: ArrayHelper.changeElement(oldState.tasks, taskIndex, taskData => ({
                        ...taskData,
                        meta: {
                            autoStatus: 'quest_complete',
                            quests: [...oldState.tasks[taskIndex].meta.quests, questIndex],
                        },
                    })),
                };
            } else {
                oldState = PlayerStateHelper.addStateTaskDatas(oldState, [
                    {
                        task: PlayerTask.SHOW_POPUP,
                        prePlayTask: false,
                        popup: PopupID.QUEST_MENU,
                        meta: {
                            autoStatus: 'quest_complete',
                            quests: [questIndex],
                        },
                    }
                ]);
            }
        }
        return oldState;
    }

    function isQuestProgressTaskOfSameType(taskData: IPlayerStateTaskData, questType: QuestType) {
        // TODO: Fix typings of taskData to have proper typing here
        return taskData.task === PlayerTask.SHOW_QUEST_PROGRESS && taskData.questData.type === questType;
    }

    function isQuestCompleteTaskOfSameType(taskData: IPlayerStateTaskData) {
        return taskData.task === PlayerTask.SHOW_POPUP && taskData.popup === PopupID.QUEST_MENU && taskData.meta.autoStatus === 'quest_complete';
    }

    /**
     * Claim quest reward and start cooldown timer
     * @deprecated Use claimQuestRewardById
     */
    export function claimQuestReward(oldState: IGameState, questIndex: number): IPlayerState {
        const quest = oldState.playerState.quests[questIndex];

        // If quest is not complete return
        if (!QuestHelper.canCompleteQuest(oldState.playerState.quests[questIndex])) {
            report('Attempting to claim quest reward before completion', {
                sampleRate: 0.1,
                once: true,
                tags: {
                    questType: quest.type,
                    tier: quest.tier,
                    progress: quest.progress,
                },
            });
            return oldState.playerState;
        }
        const questRewards = QuestHelper.getQuestRewards(quest.tier);
        if (questRewards.some(reward => isArray(reward))) {
            report('Multi reward quests is not implemented for standard quests', {
                once: true,
                sampleRate: 0.01,
                tags: {
                    /* tier: data.tier,
                    type: data.type */
                },
                fingerprint: 'claimQuestReward:MultiRewardNotImplemented',
            });
            return oldState.playerState;
        }

        // Give reward
        const playerState = InventoryStateHelper.receiveRewards(oldState.playerState, questRewards as IRewardData[], { queueNext: true });
        oldState = {
            ...oldState,
            playerState,
        }

        queueQuestCompleteEvent(quest).catch(err => {
            // error also not important
            console.error(err);
        });

        // Clear quest and set cooldown timer
        return {
            ...playerState,
            quests: ArrayHelper.changeElement(playerState.quests, questIndex, quest => {
                return QuestHelper.getNewQuestAndTrack(oldState, playerState.quests);
            }) as IPlayerStateQuestData[],
        };
    }

    export function claimQuestRewardById(state: IGameState, payload: { id: string, isEventActive: boolean }): IGameState {
        const quest = getQuestById(state, payload.id);

        if (!QuestHelper.canCompleteQuest(quest)) {
            report('Attempting to claim quest reward before completion', {
                sampleRate: 0.1,
                once: true,
                tags: {
                    questType: quest.type,
                    tier: quest.tier,
                    progress: quest.progress,
                    eventType: quest.eventType,
                },
                fingerprint: 'claimQuestRewardById:NotCompleted',
            });
            return state;
        }

        if (QuestHelper.isExpireableQuest(quest) && DateHelper.timeSince(quest.expiration) > 0) {
            report('Attempting to claim quest reward after expiration', {
                sampleRate: 0.1,
                once: true,
                tags: {
                    questType: quest.type,
                    tier: quest.tier,
                    progress: quest.progress,
                    eventType: quest.eventType,
                },
                fingerprint: 'claimQuestRewardById:Expired',
            });
            return state;
        }

        const questPoolKey = QuestHelper.getQuestPoolKeyByEventType(quest.eventType);

        const rewards = QuestHelper.getQuestRewards(quest.tier, questPoolKey);
        const targets = QuestHelper.getQuestTarget(quest.type, quest.tier, questPoolKey);

        if (isArray(targets)) {
            const claimableRewards: IRewardData[] = [];

            // Get all claimable targets
            let indexOfLastRewardCollected = quest.indexOfLastRewardCollected ?? -1;
            targets.forEach((target, index) => {
                rewards[index].forEach((reward: IRewardData) => {
                    if (reward.type === RewardType.COINS) {
                        const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));

                        reward.value = reward.value * levelCost;
                    }
                });
                const isClaimable = indexOfLastRewardCollected < index && quest.progress >= target;
                if (isClaimable && isArray(rewards[index])) {
                    // TODO: This assumes that the reward is a single reward and not an array of them
                    // @ts-ignore
                    claimableRewards.push(...rewards[index]);
                    indexOfLastRewardCollected = index;
                }
            });

            // Give rewards
            state = {
                ...state,
                playerState: InventoryStateHelper.receiveRewards(state.playerState, claimableRewards),
            };

            // Update highest claimed reward index
            state = updateQuestById(state, quest.id, {
                indexOfLastRewardCollected,
            });

            // // Close quest popup.
            // // TODO: May only be required for xmas event.
            // state = UIStateHelper.hidePopupFullState(state, PopupID.QUEST_MENU);

            if (!areAllQuestTargetsMet(quest)) {
                queueQuestCompleteEvent(quest, payload.isEventActive).catch(err => {
                    // error also not important
                    console.error(err);
                });

                return state;
            }
        } else {
            state = {
                ...state,
                // TODO: rewards *may* eventually be 2D for non-tiered missions and this would require refactoring.
                playerState: InventoryStateHelper.receiveRewards(state.playerState, rewards as IRewardData[]),
            };
        }

        queueQuestCompleteEvent(quest).catch(err => {
            // error also not important
            console.error(err);
        });

        return state;
    }

    export function getQuestRewardsById(state: IGameState, id: string): IRewardData[] {
        const quest = getQuestById(state, id);

        if (!QuestHelper.canCompleteQuest(quest)) {
            report('Attempting to claim quest reward before completion', {
                sampleRate: 0.1,
                once: true,
                tags: {
                    questType: quest.type,
                    tier: quest.tier,
                    progress: quest.progress,
                    eventType: quest.eventType,
                },
                fingerprint: 'claimQuestRewardById:NotCompleted',
            });
            return [];
        }

        if (QuestHelper.isExpireableQuest(quest) && DateHelper.timeSince(quest.expiration) > 0) {
            report('Attempting to claim quest reward after expiration', {
                sampleRate: 0.1,
                once: true,
                tags: {
                    questType: quest.type,
                    tier: quest.tier,
                    progress: quest.progress,
                    eventType: quest.eventType,
                },
                fingerprint: 'claimQuestRewardById:Expired',
            });
            return [];
        }

        const questPoolKey = QuestHelper.getQuestPoolKeyByEventType(quest.eventType);

        const rewards = QuestHelper.getQuestRewards(quest.tier, questPoolKey);
        const targets = QuestHelper.getQuestTarget(quest.type, quest.tier, questPoolKey);

        if (isArray(targets)) {
            const claimableRewards: IRewardData[] = [];

            // Get all claimable targets
            let indexOfLastRewardCollected = quest.indexOfLastRewardCollected ?? -1;
            targets.forEach((target, index) => {
                rewards[index].forEach((reward: IRewardData) => {
                    if (reward.type === RewardType.COINS) {
                        const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));

                        reward.value = reward.value * levelCost;
                    }
                });
                const isClaimable = indexOfLastRewardCollected < index && quest.progress >= target;
                if (isClaimable && isArray(rewards[index])) {
                    // TODO: This assumes that the reward is a single reward and not an array of them
                    // @ts-ignore
                    claimableRewards.push(...rewards[index]);
                    indexOfLastRewardCollected = index;
                }
            });

            return claimableRewards;
        } else {
            return rewards as IRewardData[];
        }
    }

    export function getQuestById(state: IGameState, id: string): IPlayerStateQuestData {
        const quests = [...state.playerState.quests, ...state.playerState.eventQuests];
        return quests.find(quest => quest.id === id);
    }

    export function updateQuestById(
        state: IGameState,
        id: string,
        questData: Partial<IPlayerStateQuestData>
    ): IGameState {
        const stateQuestKeys: KeyOf<Pick<IPlayerState, 'quests' | 'eventQuests'>>[] = ['quests', 'eventQuests'];
        let questIndex: number;
        // Check both standard and event quests
        for (let i = 0; i < stateQuestKeys.length; i++) {
            const stateKey = stateQuestKeys[i];
            // Get index of quest if there
            questIndex = state.playerState[stateKey].findIndex(quest => quest.id === id);
            // Update element with new data
            if (questIndex >= 0) {
                return {
                    ...state,
                    playerState: {
                        ...state.playerState,
                        [stateKey]: ArrayHelper.changeElement(state.playerState[stateKey], questIndex, quest => ({
                            ...quest,
                            ...questData,
                        })),
                    },
                };
            }
        }

        return state;
    }

    /**
     * Assigns a new quest to any empty quest with a completed timer
     */
    export function refreshQuestAfterCooldown(
        oldState: IGameState,
        questIndex: number,
    ): IGameState {
        return {
            ...oldState,
            playerState: {
                ...oldState.playerState,
                quests: ArrayHelper.changeElement(oldState.playerState.quests, questIndex, quest => {
                    return QuestHelper.getNewQuestAndTrack(oldState, oldState.playerState.quests);
                }),
            },
        };
    }

    export function isQuestsFeatureUnlocked(state: Immutable<IPlayerState>): boolean {
        return getHighestLevelCompleted(state) >= questUnlockLevel;
    }

    // helpers
    export function queueQuestsFTUXIfNeeded(playerState: IPlayerState): IPlayerState {
        if (
            getHighestLevelCompleted(playerState) >= questUnlockLevel &&
            !FTUXStateHelper.isFtuxCompleted(playerState, FTUXType.QUESTS) &&
            playerState.tasks.find(task => task.task === PlayerTask.QUESTS_FTUX) === undefined
        ) {
            const newPlayerState = {
                ...playerState,
                quests: playerState.quests.map((quest, index) => {
                    return QuestHelper.getNewQuest({
                        currentQuests: playerState.quests,
                        forcedQuestType: startingQuests[index].type,
                        forcedTier: startingQuests[index].tier,
                    });
                }),
            };
            return PlayerStateHelper.addStateTaskDatas(newPlayerState, [
                {
                    task: PlayerTask.QUESTS_FTUX,
                },
            ]);
        }
        return playerState;
    }

    export function canCompleteAnyQuest(state: IGameState): boolean {
        const eventQuest = EventStateHelper.getActiveQuest(state);

        return (
            (isQuestsFeatureUnlocked(state.playerState) &&
                state.playerState.quests.some(quest => QuestHelper.canCompleteQuest(quest))) ||
            (eventQuest && QuestHelper.canCompleteQuest(eventQuest))
        );
    }

    export function areAllQuestTargetsMet(quest: IPlayerStateQuestData): boolean {
        let targets = QuestHelper.getQuestTarget(
            quest.type,
            quest.tier,
            QuestHelper.getQuestPoolKeyByEventType(quest.eventType)
        );

        targets = isArray(targets) ? targets : [targets];

        return !targets.some(target => quest.progress < target);
    }
}
